import { unref } from 'vue'
import { useSyncQueue } from '~/composables/useSyncQueue'
import { safeToUpdate } from '~/utils/appUpdate'
import { anyHeldScans } from '~/utils/heldScans'
import { DEVICE_EVENT } from '~/utils/scanSource/deviceBridge'

/**
 * Takes a newer version of the site when there is one — at a safe moment.
 *
 * The PWA module looks for a newer version on a timer (nuxt.config,
 * `periodicSyncForUpdates`) and, because the register type is `prompt`, holds
 * it ready instead of switching by itself. This decides when to switch:
 * `utils/appUpdate.ts` has the rule and the reasons. Until the rule says yes,
 * the tablet simply carries on with the version it has.
 *
 * Does nothing under `nuxt dev`, where there is no offline copy at all.
 */

/** How often to ask whether the moment has come. */
const CONSIDER_EVERY_MS = 5_000

type Pwa = { needRefresh?: unknown, updateServiceWorker?: (reload?: boolean) => Promise<void> }

export default defineNuxtPlugin((nuxtApp) => {
  const queue = useSyncQueue()
  const router = useRouter()

  // Which build this page is, where support tools can read it without a
  // screen: the question is always "has this tablet taken the new version?".
  ;(window as unknown as { dockAutomationBuild?: string }).dockAutomationBuild
    = typeof __BUILD_STAMP__ === 'string' ? __BUILD_STAMP__ : ''

  // The last time anyone tapped, typed or scanned. Wi-Fi readings arrive on
  // the same event as scans, every few seconds, and are nobody doing anything.
  let lastTouched = Date.now()
  const touched = () => { lastTouched = Date.now() }
  window.addEventListener('pointerdown', touched, { capture: true, passive: true })
  window.addEventListener('keydown', touched, { capture: true, passive: true })
  window.addEventListener(DEVICE_EVENT, (event) => {
    if (((event as CustomEvent).detail as { type?: string } | null)?.type === 'scan') touched()
  })

  let switching = false
  let saidWaitingFor = ''

  window.setInterval(() => {
    // Looked up each time: the module's own plugin may register after this one.
    const pwa = (nuxtApp as unknown as { $pwa?: Pwa }).$pwa
    if (switching || !pwa?.updateServiceWorker || !unref(pwa.needRefresh)) return

    const verdict = safeToUpdate({
      path: router.currentRoute.value.path,
      pending: queue.pending.value,
      sending: queue.isSyncing.value,
      scansWaiting: anyHeldScans(),
      quietMs: Date.now() - lastTouched,
    })
    if (!verdict.safe) {
      // Said once per reason, not every five seconds.
      if (verdict.waitingFor !== saidWaitingFor) {
        saidWaitingFor = verdict.waitingFor
        console.info(`[update] a newer version of the site is ready; waiting for ${verdict.waitingFor}`)
      }
      return
    }

    switching = true
    console.info('[update] switching to the newer version of the site')
    void pwa.updateServiceWorker(true)
  }, CONSIDER_EVERY_MS)
})
